import React, { useState, useRef, useEffect, useLayoutEffect } from "react"
import { useHistory } from "react-router-dom"
import useDimensions from "react-use-dimensions"
import InlineButtons from "../InlineButtons/"
import "./styles.scss"
import DataLoader, { latestYear } from "../DataLoader"
import GlobalInsightsTable from "./GlobalInsightsTable"
import GlobalInsightsHistorical from "./GlobalInsightsHistorical"
import GlobalInsightsRanking from "./GlobalInsightsRanking"
import { Helmet } from "react-helmet"
import helpIcon from "../images/lightbulb-icon.svg"
import { useMemo } from "react"

export default function GlobalInsights(props) {
  const {
    selectedCountry,
    selectedYear,
    selectedFactor,
    unusedOpts,
    setOverrideMapVisibility,
    toggleGlobalInsightsInstructions,
  } = props
  const history = useHistory()
  const [selectedFilterRegion, setFilterRegion] = useState("")
  const [selectedFilterIncome, setFilterIncome] = useState("")
  const [selectedSortOrder, setSortOrder] = useState("ranking")
  const [containerRef, containerDimensions, containerNode] = useDimensions()
  const [mouseOverFilter, setMouseOverFilter] = useState(null)
  const controlsRef = useRef()

  useLayoutEffect(() => {
    if (selectedCountry) {
      return
    }
    window.scrollTo({ top: 0 })
    setTimeout(() => {
      window.scrollTo({ top: 0 })
    }, 200)
  }, [selectedCountry])

  const selectView = (view) => {
    return () => {
      const newUrlParams = {
        country: selectedCountry,
        year: selectedYear,
        factor: selectedFactor,
      }

      const baseUrl = "/global"
      let url = `${baseUrl}/${encodeURIComponent(newUrlParams.year)}`
      if (newUrlParams.country !== "") {
        url = `${url}/${encodeURIComponent(newUrlParams.country)}`
      }
      if (newUrlParams.factor !== "") {
        url = `${url}/${encodeURIComponent(newUrlParams.factor)}`
      }
      url = `${url}/${view}`
      history.push(url)
    }
  }

  const globalInsightButtons = [
    {
      label: "Table",
      onClick: selectView("table"),
      value: "table",
      view: GlobalInsightsTable,
    },
    {
      label: "Historical",
      onClick: selectView("historical"),
      value: "historical",
      view: GlobalInsightsHistorical,
      className: "overviewInstructionsHistorical",
    },
    {
      label: "Ranking",
      onClick: selectView("ranking"),
      value: "ranking",
      view: GlobalInsightsRanking,
    },
  ]

  let view = globalInsightButtons[2]

  if (unusedOpts[0]) {
    const matchingButton = globalInsightButtons.filter(
      (d) => d.value === unusedOpts[0],
    )
    if (matchingButton.length) {
      view = matchingButton[0]
    }
  }

  const updateFromSelect = (setter) => {
    return (event) => {
      if (setter === setFilterIncome) {
        setFilterRegion("")
      }
      if (setter === setFilterRegion) {
        setFilterIncome("")
      }
      setter(event.target.value)
    }
  }

  const hoverFilter = (filter) => {
    return () => {
      setMouseOverFilter(filter)
    }
  }

  const regionValues = useMemo(() => {
    return DataLoader.dataByYear[selectedYear].groups.Region.map((d) => d[0])
  }, [selectedYear])

  const regionOptions = useMemo(() => {
    return regionValues.sort().map((d) => (
      <option key={d} value={d}>
        {d}
      </option>
    ))
  }, [regionValues])

  // go with hardcoded income values now that they are consistent, avoids need to sort
  const incomeValues = ["Low", "Lower-middle", "Upper-middle", "High"]
  const incomeOptions = incomeValues.map((d) => (
    <option key={d} value={d}>
      {d}
    </option>
  ))
  if (
    selectedFilterRegion !== "" &&
    !regionValues.includes(selectedFilterRegion)
  ) {
    setFilterRegion("")
  }
  if (
    selectedFilterIncome !== "" &&
    !incomeValues.includes(selectedFilterIncome)
  ) {
    setFilterIncome("")
  }

  const scrollHeight = props.headerHeight
    ? window.innerHeight - props.headerHeight
    : null

  useEffect(() => {
    const cachedRef = controlsRef.current
    const observer = new IntersectionObserver(
      ([e]) => setOverrideMapVisibility(e.intersectionRatio < 0.99),
      { threshold: [0.99] },
    )

    observer.observe(cachedRef)

    return function () {
      observer.unobserve(cachedRef)
    }
  }, [setOverrideMapVisibility])

  let regionFilterOpacity = null
  let incomeFilterOpacity = null
  if (mouseOverFilter === null) {
    // set solely based on which filter is selected
    regionFilterOpacity = selectedFilterIncome !== "" ? 0.5 : null
    incomeFilterOpacity = selectedFilterRegion !== "" ? 0.5 : null
  } else {
    // set solely based on which filter mouse is over
    regionFilterOpacity = mouseOverFilter === "region" ? null : 0.5
    incomeFilterOpacity = mouseOverFilter === "income" ? null : 0.5
  }

  const showSortSelect = view.label === "Table"
  const sortStyle = {
    opacity: showSortSelect ? 1 : 0,
    pointerEvents: showSortSelect ? null : "none",
    display: showSortSelect ? null : "none",
    maxWidth: "11em",
  }

  const toggleHelp = () => {
    toggleGlobalInsightsInstructions()
  }
  return (
    <div className="GlobalInsights">
      <Helmet>
        <title>WJP Rule of Law Index | Global Insights</title>
        <meta
          name="description"
          content={`Explore rule of law rankings for 143 countries in the WJP Rule of Law Index. The World Justice Project Rule of Law Index® ${latestYear} is the latest report in an annual series measuring how the rule of law is experienced and perceived based on global household and expert surveys. The ${latestYear} edition covers 143 countries and jurisdictions worldwide.`}
        />
      </Helmet>
      <div
        className="content"
        ref={containerRef}
        style={{ height: scrollHeight }}
      >
        <div className="globalInsightsControls" ref={controlsRef}>
          <InlineButtons
            options={globalInsightButtons}
            style={{ flexDirection: "row" }}
            value={view.value}
          />
          <div className="filters">
            <span className="filterLabel">Filter By:</span>
            <select
              aria-label="Filter by region"
              style={{ opacity: regionFilterOpacity }}
              value={selectedFilterRegion}
              onChange={updateFromSelect(setFilterRegion)}
              onMouseOver={hoverFilter("region")}
              onMouseOut={hoverFilter(null)}
            >
              <option value="">REGION</option>
              {regionOptions}
            </select>
            <span className="filterLabel overviewInstructionsFilters">or</span>
            <select
              aria-label="Filter by income group"
              style={{ opacity: incomeFilterOpacity }}
              value={selectedFilterIncome}
              onChange={updateFromSelect(setFilterIncome)}
              onMouseOver={hoverFilter("income")}
              onMouseOut={hoverFilter(null)}
            >
              <option value="">INCOME</option>
              {incomeOptions}
            </select>
            <select
              aria-label="Sort order"
              value={selectedSortOrder}
              onChange={updateFromSelect(setSortOrder)}
              style={sortStyle}
            >
              <option value="ranking">Sort By Rank</option>
              <option value="alphabetical">Sort Alphabetically</option>
            </select>
          </div>
          <img
            src={helpIcon}
            alt="help"
            className="helpIcon"
            onClick={toggleHelp}
          />
        </div>
        <view.view
          {...props}
          selectedFilterIncome={selectedFilterIncome}
          selectedFilterRegion={selectedFilterRegion}
          selectedSortOrder={selectedSortOrder}
          containerDimensions={containerDimensions}
          containerRef={containerNode}
          controlsRef={controlsRef}
          headerHeight={props.headerHeight}
        />
      </div>
    </div>
  )
}
